import {
  Component,
  input,
  ElementRef,
  inject,
  AfterViewInit,
  OnDestroy,
  effect,
  signal,
} from "@angular/core";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./number-roll-up-wrapper.component.scss";

const digitList = "0123456789".split("");
const suffixList = "BMkT".split("");

function getListForChar(char: string): string[] {
  if (digitList.includes(char)) {
    return digitList;
  }
  if (suffixList.includes(char)) {
    return suffixList;
  }
  return [char];
}

@Component({
  selector: "app-number-roll-up-wrapper",
  templateUrl: "./number-roll-up-wrapper.component.html",
})
export class NumberRollUpWrapperComponent implements AfterViewInit, OnDestroy {
  currNum = input<string | number>();
  fontSize = input<number>();
  componentID = input<string>();

  time = 1000;
  stat: HTMLElement | null = null;
  private readonly _statReady = signal(false);
  private timers: ReturnType<typeof setTimeout>[] = [];

  private elementRef: ElementRef = inject(ElementRef);

  private get resolvedFontSize(): number {
    return this.fontSize() ?? 35;
  }

  constructor() {
    effect(() => {
      const val = this.currNum();
      const fs = this.resolvedFontSize;
      if (this._statReady()) {
        this.setCustomisedStyleVariable("font-size", fs);
        this.setup(val ?? "");
      }
    });
  }

  ngAfterViewInit() {
    const componentId = this.componentID();
    if (!componentId) {
      return;
    }

    this.stat = this.elementRef.nativeElement.querySelector(
      `#${componentId}`,
    ) as HTMLElement | null;
    this._statReady.set(true);
  }

  ngOnDestroy() {
    this.timers.forEach((t) => clearTimeout(t));
  }

  setup(startNum: string | number) {
    this.timers.forEach((t) => clearTimeout(t));
    this.timers = [];

    const value = typeof startNum === "number" ? startNum.toString() : (startNum ?? "");
    const digits = value.split("");

    if (!this.stat) {
      return;
    }

    this.stat.replaceChildren();

    const spans: HTMLSpanElement[] = [];
    for (let i = 0; i < digits.length; i++) {
      const span = this.addDigit(digits[i]);
      if (span) {
        spans.push(span);
      }
    }

    // Single 0ms timeout triggers CSS transitions for all columns in one style recalculation
    this.timers.push(setTimeout(() => spans.forEach((s) => s.classList.add("visible")), 0));
    this.timers.push(setTimeout(() => this.scrollNumber(digits), this.time + 50));
  }

  scrollNumber(digits: string[]) {
    if (!this.stat) {
      return;
    }

    const ticks = Array.from(this.stat.querySelectorAll<HTMLElement>("span[data-value]"));

    // Batch all layout reads before any writes to avoid layout thrashing
    const offsets = ticks.map((tick, i) => {
      const targetChar = digits[i] ?? "";
      const charList = getListForChar(targetChar);
      const targetIndex = charList.indexOf(targetChar);
      if (targetIndex < 0) {
        return { tick, offset: 0, width: tick.offsetWidth };
      }
      let offset = 0;
      for (let j = 0; j < targetIndex; j++) {
        offset += (tick.children[j] as HTMLElement).offsetHeight;
      }
      return { tick, offset, width: tick.offsetWidth };
    });

    // Batch all transform writes
    for (const { tick, offset } of offsets) {
      tick.style.transform = `translateY(-${offset}px)`;
    }

    const totalWidth = offsets.reduce((sum, { width }) => sum + width, 0);
    this.timers.push(
      setTimeout(() => {
        if (this.stat) {
          this.stat.style.width = `${Math.max(totalWidth, this.resolvedFontSize)}px`;
        }
      }, 100),
    );
  }

  addDigit(targetChar: string): HTMLSpanElement | null {
    if (!this.stat) {
      return null;
    }

    const charList = getListForChar(targetChar);
    const fontSize = this.resolvedFontSize;

    const outerSpan = document.createElement("span");
    outerSpan.style.transform = "translateY(0px)";
    outerSpan.style.fontSize = this.setSizeUnit(fontSize);
    outerSpan.style.fontWeight = "500";
    outerSpan.dataset["value"] = targetChar;

    for (const x of charList) {
      const inner = document.createElement("span");
      inner.textContent = x;
      outerSpan.appendChild(inner);
    }

    this.stat.appendChild(outerSpan);
    return outerSpan;
  }

  setSizeUnit(size: number) {
    return `${size}px`;
  }

  setCustomisedStyleVariable(cssVariable: string, size: number) {
    const value = this.setSizeUnit(size);
    this.elementRef.nativeElement
      ?.querySelector(".number")
      ?.style.setProperty(`--${cssVariable}`, value);
  }
}
